'use strict';

/**
 * RecentlyViewed — tracks recently viewed product IDs/handles in localStorage.
 *
 * Storage key : 'visionary-recently-viewed'
 * Max items   : 8
 *
 * On product pages : reads the product handle from [data-recently-viewed-product]
 *                    and adds it to localStorage on page load.
 *
 * On recently-viewed sections : fetches product card HTML via:
 *   GET /products/{handle}?view=card
 * or renders from a [data-recently-viewed-grid] data attribute if present.
 *
 * Emits custom event 'recently-viewed:updated' on window after any change.
 */
(function () {
  var STORAGE_KEY = 'visionary-recently-viewed';
  var MAX_ITEMS = 8;

  // ---- Storage helpers ------------------------------------------------------
  function getItems() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveItems(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // localStorage may be unavailable — fail silently
    }
  }

  function addItem(handle) {
    if (!handle || typeof handle !== 'string') return;
    var items = getItems();
    // Remove duplicate if present
    items = items.filter(function (h) { return h !== handle; });
    // Prepend current
    items.unshift(handle);
    // Cap to max
    if (items.length > MAX_ITEMS) {
      items = items.slice(0, MAX_ITEMS);
    }
    saveItems(items);
    window.dispatchEvent(new CustomEvent('recently-viewed:updated', { detail: { items: items } }));
  }

  // ---- Track current product page ------------------------------------------
  function trackCurrentProduct() {
    var el = document.querySelector('[data-recently-viewed-product]');
    if (!el) return;
    var handle = el.getAttribute('data-recently-viewed-product');
    if (handle) {
      // Slight delay so the page registers a "view" before we update storage
      setTimeout(function () { addItem(handle); }, 500);
    }
  }

  // ---- Render recently-viewed section(s) ------------------------------------
  function renderSections() {
    var sections = document.querySelectorAll('[data-recently-viewed-section]');
    if (!sections.length) return;

    var items = getItems();

    // Exclude the current product if we are on a product page
    var currentHandle = '';
    var trackEl = document.querySelector('[data-recently-viewed-product]');
    if (trackEl) currentHandle = trackEl.getAttribute('data-recently-viewed-product') || '';

    var handles = items.filter(function (h) { return h !== currentHandle; });

    sections.forEach(function (section) {
      if (!handles.length) {
        showEmpty(section);
        return;
      }
      fetchCards(section, handles);
    });
  }

  function showEmpty(section) {
    var grid = section.querySelector('.recently-viewed__grid');
    var empty = section.querySelector('.recently-viewed__empty');
    if (grid) grid.style.display = 'none';
    if (empty) empty.classList.add('is-visible');
  }

  function fetchCards(section, handles) {
    var grid = section.querySelector('.recently-viewed__grid');
    if (!grid) return;

    // Determine how many columns to show based on data attribute
    var limit = parseInt(section.getAttribute('data-limit') || '4', 10);
    var displayHandles = handles.slice(0, limit);

    if (!displayHandles.length) {
      showEmpty(section);
      return;
    }

    // Show loading placeholders
    grid.innerHTML = displayHandles.map(function () {
      return '<div class="recently-viewed__placeholder" aria-hidden="true"></div>';
    }).join('');

    var loaded = 0;
    var results = new Array(displayHandles.length).fill(null);
    var hasAny = false;

    displayHandles.forEach(function (handle, index) {
      fetch('/products/' + encodeURIComponent(handle) + '?view=card', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Not found');
          return res.text();
        })
        .then(function (html) {
          results[index] = html;
          hasAny = true;
        })
        .catch(function () {
          results[index] = null;
        })
        .finally(function () {
          loaded++;
          if (loaded === displayHandles.length) {
            renderResults(grid, results, section, hasAny);
          }
        });
    });
  }

  function renderResults(grid, results, section, hasAny) {
    if (!hasAny) {
      showEmpty(section);
      return;
    }

    grid.innerHTML = results
      .filter(function (html) { return html !== null; })
      .join('');

    // Dispatch event so other JS (e.g. variant selects) can init on new cards
    document.dispatchEvent(new CustomEvent('recently-viewed:rendered', {
      detail: { section: section },
      bubbles: true
    }));
  }

  // ---- Public API -----------------------------------------------------------
  window.Visionary = window.Visionary || {};
  window.Visionary.RecentlyViewed = {
    add: addItem,
    get: getItems,
    clear: function () {
      saveItems([]);
      window.dispatchEvent(new CustomEvent('recently-viewed:updated', { detail: { items: [] } }));
    }
  };

  // ---- Initialise -----------------------------------------------------------
  function init() {
    trackCurrentProduct();
    renderSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
